import * as THREE from 'three';
import { AssetLoader } from '../loading/AssetLoader';
import { CITY_GRID, tileToWorld } from './CityMap';
import { TILE_SIZE, GRID_SIZE, KENNEY_SCALE } from '../config';
import type { TileType, AABB } from '../types';
import { CollisionWorld } from '../collision/CollisionWorld';
import { createSeededRandom } from '../utils/math';

const rng = createSeededRandom(456);

export class CityBuilder {
  private loader: AssetLoader;
  private collisionWorld: CollisionWorld;
  private buildingGroup = new THREE.Group();
  private roadGroup = new THREE.Group();
  private detailGroup = new THREE.Group();

  constructor(scene: THREE.Scene, loader: AssetLoader, collisionWorld: CollisionWorld) {
    this.loader = loader;
    this.collisionWorld = collisionWorld;
    scene.add(this.buildingGroup);
    scene.add(this.roadGroup);
    scene.add(this.detailGroup);
  }

  buildRoads() {
    this.clearGroup(this.roadGroup);

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const tile = CITY_GRID[row][col];
        if (!this.isRoadType(tile.type)) continue;

        const [x, z] = tileToWorld(row, col, TILE_SIZE);
        const roadKey = this.getRoadModelKey(tile.type);
        if (!roadKey) continue;

        const model = this.loader.getModel(roadKey, 1.0);
        if (!model) continue;

        // fitMax 1.0 = edge-to-edge tiling (roundabout 3x3 → scaled to 1 tile)
        model.scale.setScalar(KENNEY_SCALE);
        model.position.set(x, 0, z);
        model.rotation.y = THREE.MathUtils.degToRad(tile.rotation);
        model.traverse((c) => {
          if (c instanceof THREE.Mesh) {
            c.receiveShadow = true;
          }
        });
        this.roadGroup.add(model);
      }
    }
  }

  async buildBuildings() {
    this.clearGroup(this.buildingGroup);
    this.collisionWorld.clearBuildings();

    const TILES_PER_CHUNK = 30;
    const SHRINK = 0.3;
    let placed = 0;

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const tile = CITY_GRID[row][col];
        if (!this.isBuildingType(tile.type)) continue;
        if (!tile.model) continue;

        const [x, z] = tileToWorld(row, col, TILE_SIZE);
        const category = this.getCategoryForType(tile.type);
        const key = `${category}/${tile.model}`;

        const model = this.loader.getModel(key, 0.9);
        if (!model) continue;

        model.scale.setScalar(KENNEY_SCALE);
        model.position.set(x, 0, z);
        model.rotation.y = THREE.MathUtils.degToRad(tile.rotation);

        model.traverse((c) => {
          if (c instanceof THREE.Mesh) {
            c.castShadow = true;
            c.receiveShadow = true;
          }
        });

        this.buildingGroup.add(model);

        const dims = this.loader.getModelDimensions(key, 0.9);
        if (dims) {
          const rot = ((tile.rotation % 360) + 360) % 360;
          const swapAxes = rot === 90 || rot === 270;
          const extentX = (swapAxes ? dims.z : dims.x) * KENNEY_SCALE;
          const extentZ = (swapAxes ? dims.x : dims.z) * KENNEY_SCALE;
          const halfX = extentX / 2;
          const halfZ = extentZ / 2;
          const aabb: AABB = {
            minX: x - halfX + SHRINK,
            maxX: x + halfX - SHRINK,
            minY: 0,
            maxY: dims.y * KENNEY_SCALE,
            minZ: z - halfZ + SHRINK,
            maxZ: z + halfZ - SHRINK,
          };
          this.collisionWorld.addAABB(aabb);
        }

        placed++;
        if (placed % TILES_PER_CHUNK === 0) {
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        }
      }
    }
  }

  buildDetails() {
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const tile = CITY_GRID[row][col];
        const [x, z] = tileToWorld(row, col, TILE_SIZE);

        if (tile.type === 'road_crossroad' || tile.type === 'road_intersection') {
          // Place light at a random corner of the intersection (on the sidewalk)
          const cornerX = rng() > 0.5 ? 1 : -1;
          const cornerZ = rng() > 0.5 ? 1 : -1;
          this.placeDetail(
            'road/light-square',
            x + cornerX * TILE_SIZE * 0.45,
            z + cornerZ * TILE_SIZE * 0.45,
            rng() * Math.PI * 2,
          );
        }

        if (tile.type === 'building_commercial') {
          if (rng() > 0.4) {
            const awningKey = rng() > 0.5 ? 'commercial/detail-awning' : 'commercial/detail-awning-wide';
            this.placeDetail(awningKey, x, z, THREE.MathUtils.degToRad(tile.rotation));
          }
          if (rng() > 0.7) {
            const parasolKey = rng() > 0.5 ? 'commercial/detail-parasol-a' : 'commercial/detail-parasol-b';
            const ox = (rng() - 0.5) * TILE_SIZE * 0.4;
            const oz = (rng() - 0.5) * TILE_SIZE * 0.4;
            this.placeDetail(parasolKey, x + ox, z + oz, rng() * Math.PI * 2);
          }
        }

        if (tile.type === 'building_industrial' && rng() > 0.5) {
          const chimneys = ['industrial/chimney-basic', 'industrial/chimney-small', 'industrial/chimney-medium'];
          this.placeDetail(chimneys[Math.floor(rng() * chimneys.length)], x, z, 0);
        }

        if (tile.type === 'building_suburban') {
          if (rng() > 0.5) {
            this.placeDetail('suburban/fence', x, z, THREE.MathUtils.degToRad(tile.rotation));
          }
          if (rng() > 0.6) {
            this.placeDetail('suburban/path-short', x, z, THREE.MathUtils.degToRad(tile.rotation));
          }
          if (rng() > 0.7) {
            this.placeDetail('suburban/driveway-short', x, z, THREE.MathUtils.degToRad(tile.rotation));
          }
          if (rng() > 0.5) {
            const treeKey = rng() > 0.5 ? 'suburban/tree-large' : 'suburban/tree-small';
            const ox = (rng() - 0.5) * TILE_SIZE * 0.4;
            const oz = (rng() - 0.5) * TILE_SIZE * 0.4;
            this.placeDetail(treeKey, x + ox, z + oz, rng() * Math.PI * 2);
          }
        }

        if (tile.type === 'grass' && this.nearIndustrial(row, col) && rng() > 0.8) {
          this.placeDetail(
            'road/construction-cone',
            x + (rng() - 0.5) * TILE_SIZE * 0.4,
            z + (rng() - 0.5) * TILE_SIZE * 0.4,
            0,
          );
        }
      }
    }

    // Signs at city entry roads
    const entrySign = 'road/sign-highway';
    if (this.loader.has(entrySign)) {
      const [x6, z0] = tileToWorld(0, 6, TILE_SIZE);
      const [x9, z0b] = tileToWorld(0, 9, TILE_SIZE);
      this.placeDetail(entrySign, x6, z0 - 1, 0);
      this.placeDetail(entrySign, x9, z0b - 1, 0);
    }
  }

  private placeDetail(key: string, x: number, z: number, rotY: number) {
    const model = this.loader.getModel(key);
    if (!model) return;
    // Uniform scale for details too — Kenney proportions stay consistent
    model.scale.setScalar(KENNEY_SCALE);
    model.position.set(x, 0, z);
    model.rotation.y = rotY;
    model.traverse((c) => {
      if (c instanceof THREE.Mesh) {
        c.castShadow = true;
        c.receiveShadow = true;
      }
    });
    this.detailGroup.add(model);
  }

  private nearIndustrial(row: number, col: number): boolean {
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const r = row + dr;
        const c = col + dc;
        if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
          if (CITY_GRID[r][c].type === 'building_industrial') return true;
        }
      }
    }
    return false;
  }

  private clearGroup(group: THREE.Group) {
    while (group.children.length > 0) {
      const child = group.children[0];
      group.remove(child);
    }
  }

  private isRoadType(type: TileType): boolean {
    return type.startsWith('road_');
  }

  private isBuildingType(type: TileType): boolean {
    return type.startsWith('building_');
  }

  private getRoadModelKey(type: TileType): string | null {
    const map: Record<string, string> = {
      road_straight: 'road/road-straight',
      road_bend: 'road/road-bend-sidewalk',
      road_crossroad: 'road/road-crossroad-path',
      road_intersection: 'road/road-intersection-path',
      road_roundabout: 'road/road-roundabout',
      road_end: 'road/road-end-round',
    };
    return map[type] ?? null;
  }

  private getCategoryForType(type: TileType): string {
    if (type === 'building_commercial' || type === 'building_skyscraper') return 'commercial';
    if (type === 'building_industrial') return 'industrial';
    return 'suburban';
  }
}
